import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import type { CostSplit, RunPaidBy } from "../../shared/types";
import { Icon } from "./Icon";
import { IconButton } from "./IconButton";
import { cost } from "./ui";

const dollars = (usd: number) => `$${usd.toFixed(2)}`;
/** Room kept between the pop-up and the edge of the screen, and between it and its icon. */
const GUTTER = 8;
const GAP = 6;

/** What paid for one run, in words. Null for a run that never started, which paid for nothing. */
export const paidByWords = (paidBy: RunPaidBy | null | undefined) =>
  paidBy === "api_key" ? "Billed on an API key" : paidBy === "subscription" ? "Estimated on a subscription, not billed" : null;

/** One run's cost in a row of runs, what paid for it on hover. A run's own page says it in full. */
export function RunCost({ usd, paidBy }: { usd: number | null | undefined; paidBy: RunPaidBy | null | undefined }) {
  return <span title={paidByWords(paidBy) ?? undefined}>{cost(usd)}</span>;
}

/**
 * The small icon beside a total of cost. It opens on hover and on keyboard focus, and on a tap on a phone, and says how
 * much of that total was billed on API keys and how much is only an estimate of runs on subscriptions. `toolCalls`, where
 * the caller has it, adds the calls on API-call connectors as a count: they are not priced yet, so never as an amount.
 * Drawn on the page's top layer, so a card that clips what overflows it never cuts it off.
 */
export function CostBreakdown({ billedUsd, estimatedUsd, toolCalls }: CostSplit & { toolCalls?: number }) {
  const [open, setOpen] = useState(false);
  const [place, setPlace] = useState<CSSProperties | null>(null);
  const id = useId();
  const anchor = useRef<HTMLSpanElement>(null);
  const tip = useRef<HTMLDivElement>(null);
  // Which pointer pressed the icon, until its click: a press also focuses it, and that focus must not open what the tap decides.
  const pressed = useRef<string | null>(null);

  useLayoutEffect(() => {
    if (!open) return setPlace(null);
    const at = anchor.current!.getBoundingClientRect();
    const box = tip.current!.getBoundingClientRect();
    const below = at.bottom + GAP;
    setPlace({
      left: Math.max(GUTTER, Math.min(at.left + at.width / 2 - box.width / 2, window.innerWidth - box.width - GUTTER)),
      top: below + box.height > window.innerHeight - GUTTER ? Math.max(GUTTER, at.top - box.height - GAP) : below,
    });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const key = (e: KeyboardEvent) => e.key === "Escape" && close();
    const outside = (e: PointerEvent) => !anchor.current?.contains(e.target as Node) && close();
    document.addEventListener("keydown", key);
    document.addEventListener("pointerdown", outside);
    // It stays where the icon was, so it goes when the page moves under it.
    window.addEventListener("scroll", close, true);
    return () => {
      document.removeEventListener("keydown", key);
      document.removeEventListener("pointerdown", outside);
      window.removeEventListener("scroll", close, true);
    };
  }, [open]);

  return (
    <span ref={anchor} className="inline-flex shrink-0 align-middle">
      <IconButton
        label="Cost breakdown"
        title={undefined}
        size="xs"
        fill="none"
        aria-expanded={open}
        aria-describedby={open ? id : undefined}
        onPointerEnter={(e) => e.pointerType === "mouse" && setOpen(true)}
        onPointerLeave={(e) => e.pointerType === "mouse" && setOpen(false)}
        onPointerDown={(e) => { pressed.current = e.pointerType; }}
        onFocus={() => { if (!pressed.current) setOpen(true); }}
        onBlur={() => { pressed.current = null; setOpen(false); }}
        onClick={(e) => {
          // Beside a cost in a row that is itself a link: only the breakdown opens.
          e.preventDefault();
          e.stopPropagation();
          const hovering = pressed.current === "mouse";
          pressed.current = null;
          setOpen((was) => hovering || !was);
        }}
      >
        <Icon name="breakdown" className="size-3.5" />
      </IconButton>
      {open && createPortal(
        <div
          ref={tip}
          id={id}
          role="tooltip"
          style={place ?? { left: 0, top: 0, visibility: "hidden" }}
          className="pointer-events-none fixed z-50 w-80 max-w-[calc(100vw-2rem)] rounded-lg border border-line bg-raised p-3 text-xs text-muted shadow-lg"
        >
          <dl className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-1.5">
            <dt>Billed on API keys</dt>
            <dd className="text-right text-fg tabular-nums">{dollars(billedUsd)}</dd>
            <dt>Estimated on subscriptions</dt>
            <dd className="text-right text-fg tabular-nums">{dollars(estimatedUsd)}, not billed</dd>
            {toolCalls !== undefined && (
              <>
                <dt>Tool calls on API-call connectors</dt>
                <dd className="text-right text-fg tabular-nums">{toolCalls.toLocaleString()}, not priced yet</dd>
              </>
            )}
          </dl>
        </div>,
        document.body,
      )}
    </span>
  );
}
